import React, { useCallback, useRef, useState } from 'react';
import { getIn } from 'formik';
import Chip from '@material-ui/core/Chip';
import Popover from '@material-ui/core/Popover';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';

import icPlus from 'core/assets/svg/ic-plus.svg';
import { FieldElementPropTypes } from '../propTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    padding: '0 30px 0 7px',
    minHeight: 36,
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: theme.colorPalette.white,
    border: 'none',
    borderRadius: 0,
    marginTop: theme.spacing(4),
    position: 'relative',
    '&.Mui-error': {
      border: `1px solid ${theme.colorPalette.red_50}`
    }
  },
  chipContainerDisabled: {
    cursor: 'default',
    opacity: 0.4
  },
  chipContainerError: {
    border: `1px solid ${theme.colorPalette.red_50}`
  },
  chip: {
    margin: 2,
    backgroundColor: theme.colorPalette.beige_50,
    color: theme.colorPalette.green_90
  },
  placeholder: {
    color: theme.colorPalette.beige_50_op_70,
    fontSize: 14
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  menuItemSelected: {
    fontWeight: 600,
    backgroundColor: 'rgba(114, 120, 49, 0.15) !important'
  },
  labelRoot: {
    color: theme.colors.text_secondary,
    whiteSpace: 'nowrap',
    zIndex: 1
  },
  labelFocused: {
    color: `${theme.colors.text_secondary} !important`
  },
  labelShrink: {
    width: '100%',
    textAlign: 'initial',
    overflow: 'visible'
  },
  groupHeader: {
    color: theme.colorPalette.green_75,
    fontWeight: 600,
    fontSize: 10,
    textTransform: 'uppercase',
    pointerEvents: 'none',
    paddingTop: 8,
    paddingBottom: 4
  },
  selectAllItem: {
    color: theme.colorPalette.orange_50,
    fontWeight: 600
  },
  caret: {
    width: 24,
    height: 24,
    position: 'absolute',
    top: 7,
    right: 6,
    cursor: 'pointer'
  }
}));

const ChipArrayField = ({
  field: {
    id,
    name,
    label,
    options = [],
    value = [],
    disabled,
    onChange,
    placeholder,
    canDeleteChips = false,
    emptyMessage,
    ...props
  },
  ctx: { setFieldValue, setFieldTouched, touched, errors }
}) => {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedValues = Array.isArray(value) ? value : [];
  const fieldName = name || id;
  const hasError = getIn(touched, fieldName) && !!getIn(errors, fieldName);
  const errorText = getIn(touched, fieldName) ? getIn(errors, fieldName) : undefined;

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setFieldTouched(fieldName, true, false);
  }, [fieldName, setFieldTouched]);

  const handleToggleOption = useCallback(
    (optionId) => {
      const isSelected = selectedValues.includes(optionId);
      const newValue = isSelected ? selectedValues.filter((v) => v !== optionId) : [...selectedValues, optionId];

      setFieldValue(fieldName, newValue);
      setFieldTouched(fieldName, true, false);
      if (onChange) {
        onChange(newValue, fieldName);
      }
    },
    [selectedValues, fieldName, setFieldValue, setFieldTouched, onChange]
  );

  const selectableOptions = options.filter((o) => !o.disabled && !o.isGroupHeader);
  // Options may opt out of "Automatically associate to all items" (e.g. farm RPS sub-items nested under
  // a structure — #34881) while staying individually selectable. Toggling "all" adds/removes only these.
  const selectAllOptions = selectableOptions.filter((o) => !o.excludeFromSelectAll);
  const allSelected = selectAllOptions.length > 0 && selectAllOptions.every((o) => selectedValues.includes(o.id));

  const handleToggleAll = useCallback(() => {
    const allIds = selectAllOptions.map((o) => o.id);
    // Preserve any excluded selections (RPS chips) the agent picked by hand; only flip the "all" set.
    const newValue = allSelected
      ? selectedValues.filter((v) => !allIds.includes(v))
      : Array.from(new Set([...selectedValues, ...allIds]));
    setFieldValue(fieldName, newValue);
    setFieldTouched(fieldName, true, false);
    if (onChange) {
      onChange(newValue, fieldName);
    }
  }, [allSelected, selectAllOptions, selectedValues, fieldName, setFieldValue, setFieldTouched, onChange]);

  const handleDeleteChip = useCallback(
    (optionId) => (e) => {
      e.stopPropagation();
      if (disabled) {
        return;
      }
      const newValue = selectedValues.filter((v) => v !== optionId);
      setFieldValue(fieldName, newValue);
      setFieldTouched(fieldName, true, false);
      if (onChange) {
        onChange(newValue, fieldName);
      }
    },
    [selectedValues, fieldName, disabled, setFieldValue, setFieldTouched, onChange]
  );

  return (
    <FormControl fullWidth error={hasError} className={classes.root} {...props}>
      {label && (
        <InputLabel
          shrink
          classes={{ root: classes.labelRoot, focused: classes.labelFocused, shrink: classes.labelShrink }}
        >
          {label}
        </InputLabel>
      )}
      <div
        ref={anchorRef}
        className={`${classes.chipContainer} ${disabled ? classes.chipContainerDisabled : ''} ${hasError ? classes.chipContainerError : ''}`}
        onClick={handleOpen}
      >
        {selectedValues.length === 0 && placeholder && <span className={classes.placeholder}>{placeholder}</span>}
        <img src={icPlus} alt="expand" className={classes.caret} />
        {selectedValues.map((selectedId) => {
          const option = options.find((o) => o.id === selectedId);
          if (!option) {
            return null;
          }
          return (
            <Chip
              key={selectedId}
              label={option.value}
              size="small"
              className={classes.chip}
              onDelete={canDeleteChips && !disabled ? handleDeleteChip(selectedId) : undefined}
              disabled={disabled}
            />
          );
        })}
      </div>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: { maxHeight: 300, width: anchorRef.current?.offsetWidth }
        }}
      >
        <MenuList>
          {selectableOptions.length === 0 && emptyMessage ? (
            <MenuItem disabled style={{ whiteSpace: 'normal', color: '#52583c' }}>
              {emptyMessage}
            </MenuItem>
          ) : (
            <>
              {options.map((option) => {
                if (option.isGroupHeader) {
                  return (
                    <MenuItem key={option.id} className={classes.groupHeader} disabled>
                      {option.value}
                    </MenuItem>
                  );
                }
                const isSelected = selectedValues.includes(option.id);
                return (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleToggleOption(option.id)}
                    className={`${classes.menuItem} ${isSelected ? classes.menuItemSelected : ''}`}
                    disabled={option.disabled}
                  >
                    {option.value}
                  </MenuItem>
                );
              })}
              <MenuItem onClick={handleToggleAll} className={classes.selectAllItem}>
                Automatically associate to all items
              </MenuItem>
            </>
          )}
        </MenuList>
      </Popover>
    </FormControl>
  );
};

ChipArrayField.propTypes = FieldElementPropTypes;

export default ChipArrayField;
